import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { callMethod } from '../../../lib/api';
import { INSIGHTS_AGENT_NAME } from '../lib/agentBranding';
import { formatRange } from '../lib/contextSummary';
import {
  applyPrefsToSituations,
  dismissSituation,
  loadAgentPrefs,
  type AgentPrefs,
} from '../lib/agentPrefs';
import {
  canDecompose,
  displaySplitEffects,
  dominantSplitLabel,
  effectUnitLabel,
  formatEffect,
  maxAbsSplitEffect,
  whyForExpandedCard,
  contribMetricKey,
} from '../lib/contribDisplay';
import type {
  ContributionSummary,
  MetricContributionPayload,
  SituationCard,
  SituationEvidence,
  SituationPackPayload,
  SituationSeverity,
} from '../lib/situationTypes';
import type {
  AgentFocus,
  ReservationSourceId,
  ReservationStatusGroup,
} from '../types';
import type { UserSituationProfile } from '../lib/userSituationProfile';
import { Icon } from './shared/Icon';
import { OpsHoverTipPortal, useOpsHoverTip } from './shared/OpsHoverTip';

const CARD_CAP = 5;
const CHIP_CAP = 4;

/** Codes that replace the brief entirely (not soft compare advisories). */
const HARD_QUALITY_CODES = new Set([
  'stale_data',
  'empty_scope',
  'null_spike',
  'duplicate_keys',
  'no_pos',
  'mixed_currency',
  'definition_fragile',
  'low_sample',
]);

type ContribState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  why?: string | null;
  contribution?: ContributionSummary | null;
  refused?: boolean;
  error?: string;
};

function bandOf(card: SituationCard): 'trend' | 'signal' {
  if (card.layer === 'trend' || card.layer === 'signal') return card.layer;
  if (card.severity === 'critical' || card.severity === 'watch') return 'signal';
  return 'trend';
}

function severityTone(s: SituationSeverity): 'risk' | 'win' | 'watch' {
  if (s === 'critical' || s === 'watch') return 'risk';
  if (s === 'opportunity') return 'win';
  return 'watch';
}

function severityIcon(s: SituationSeverity): string {
  if (s === 'critical') return 'flag';
  if (s === 'opportunity') return 'trendUp';
  if (s === 'watch') return 'activity';
  return 'info';
}

function overlapsInsight(chip: string, insight: string): boolean {
  const a = chip.toLowerCase().replace(/[^\w\s]/g, ' ').trim();
  const b = insight.toLowerCase().replace(/[^\w\s]/g, ' ').trim();
  if (!a || !b) return false;
  if (a.includes(b) || b.includes(a)) return true;
  const aTok = a.split(/\s+/).filter(t => t.length > 3);
  const bSet = new Set(b.split(/\s+/).filter(t => t.length > 3));
  if (aTok.length === 0) return false;
  const hit = aTok.filter(t => bSet.has(t)).length;
  return hit >= Math.min(3, aTok.length);
}

function digInChips(
  situations: SituationCard[],
  profile: UserSituationProfile | null | undefined,
): string[] {
  const curated = profile?.chips ?? [];
  const out: string[] = [];
  for (const chip of curated) {
    if (out.length >= CHIP_CAP) break;
    if (situations.some(s => overlapsInsight(chip, s.insight))) continue;
    if (out.some(x => x.toLowerCase() === chip.toLowerCase())) continue;
    out.push(chip);
  }
  return out;
}

function shortText(s: string, max = 72): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trimEnd()}…`;
}

/** Split "Cancels up 43% vs 30 March – 31 May 2026" into trend + compare range. */
function splitInsightVs(insight: string): { trend: string; vs: string | null } {
  const idx = insight.lastIndexOf(' vs ');
  if (idx <= 0) return { trend: insight, vs: null };
  const trend = insight.slice(0, idx).trim();
  const vs = insight.slice(idx + 4).trim();
  if (!trend || !vs) return { trend: insight, vs: null };
  return { trend, vs };
}

function SplitLane({
  kind,
  label,
  value,
  maxAbs,
  unitLab,
}: {
  kind: 'mix' | 'rate';
  label: string;
  value: number;
  maxAbs: number;
  unitLab: string;
}) {
  const abs = Math.abs(value);
  const halfPct = abs < 1e-9 ? 0 : Math.max((abs / maxAbs) * 50, 3);
  const side = value > 0 ? 'pos' : value < 0 ? 'neg' : 'flat';
  return (
    <div className={`ph-contrib-lane is-${kind}`}>
      <span className="ph-contrib-lane-lab">{label}</span>
      <span className="ph-contrib-track" aria-hidden>
        {side !== 'flat' ? (
          <span
            className={`ph-contrib-fill is-${side}`}
            style={
              side === 'pos'
                ? { left: '50%', width: `${halfPct}%` }
                : { right: '50%', width: `${halfPct}%` }
            }
          />
        ) : (
          <span className="ph-contrib-zero" />
        )}
      </span>
      <span className="ph-contrib-lane-val">
        {formatEffect(value, unitLab, { unitSuffix: false })}
      </span>
    </div>
  );
}

function ContributionBars({
  rows,
  tip,
  showValues = false,
  mixLab = 'Share',
  rateLab = 'Volume',
  unit,
}: {
  rows: NonNullable<ContributionSummary['contributors']>;
  tip: ReturnType<typeof useOpsHoverTip>;
  showValues?: boolean;
  mixLab?: string;
  rateLab?: string;
  unit?: string | null;
}) {
  const unitLab = effectUnitLabel(unit);
  const prepared = rows.slice(0, 3).map(row => {
    const split = displaySplitEffects(row.mixEffect, row.rateEffect, unitLab);
    return { row, split };
  });
  // Drop near-flat buckets so Staff-at-zero doesn't steal the story.
  const shown = prepared.filter(
    ({ split }) =>
      Math.abs(split.mix) >= 0.05 ||
      Math.abs(split.rate) >= 0.05 ||
      Math.abs(split.net) >= 0.05,
  );
  const visible = (shown.length > 0 ? shown : prepared).slice(0, 2);
  const maxAbs = maxAbsSplitEffect(
    visible.map(({ row, split }) => ({
      ...row,
      mixEffect: split.mix,
      rateEffect: split.rate,
      totalEffect: split.net,
    })),
  );
  return (
    <ul className="ph-contrib-list" aria-label={i18n.t('performance:situation.whyMoved')}>
      {visible.map(({ row, split }) => {
        const driver = dominantSplitLabel(
          split.mix,
          split.rate,
          mixLab,
          rateLab,
        );
        const detail = i18n.t('performance:situation.contribDetail', {
          label: row.label,
          mixLab,
          mix: formatEffect(split.mix, unitLab),
          rateLab,
          rate: formatEffect(split.rate, unitLab),
          net: formatEffect(split.net, unitLab),
        });
        return (
          <li
            key={row.id || row.label}
            className="ph-contrib-row"
            onMouseEnter={e =>
              tip.showAnchored(
                e.currentTarget,
                { title: row.label, text: detail },
                'above',
              )
            }
            onMouseLeave={tip.hide}
          >
            <div className="ph-contrib-head">
              <span className="ph-contrib-label">{row.label}</span>
            </div>
            <div className="ph-contrib-lanes">
              <SplitLane
                kind="mix"
                label={mixLab}
                value={split.mix}
                maxAbs={maxAbs}
                unitLab={unitLab}
              />
              <SplitLane
                kind="rate"
                label={rateLab}
                value={split.rate}
                maxAbs={maxAbs}
                unitLab={unitLab}
              />
            </div>
            {showValues ? (
              <div className="ph-contrib-sum">
                <span className="ph-contrib-sum-eq" aria-hidden>
                  =
                </span>
                <span className="ph-contrib-vals">
                  {formatEffect(split.net, unitLab)}
                  <span className="ph-contrib-vals-lab">
                    {i18n.t('performance:situation.net')}
                  </span>
                </span>
                {driver ? (
                  <span className="ph-contrib-driver">
                    {i18n.t('performance:situation.mostly', {
                      driver: driver.toLowerCase(),
                    })}
                  </span>
                ) : null}
              </div>
            ) : driver ? (
              <p className="ph-contrib-driver">
                {i18n.t('performance:situation.mostly', {
                  driver: driver.toLowerCase(),
                })}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function ContributionPanel({
  state,
  fallbackWhy,
  decomposable,
  tip,
  showValues = false,
  recommendedAction,
  splitBlockedMessage,
}: {
  state: ContribState | undefined;
  fallbackWhy: string;
  decomposable: boolean;
  tip: ReturnType<typeof useOpsHoverTip>;
  showValues?: boolean;
  recommendedAction?: string;
  /** When set, contribution arrived but must not be charted (sign/baseline). */
  splitBlockedMessage?: string;
}) {
  if (!decomposable) {
    if (!recommendedAction) {
      return null;
    }
    return (
      <div className="ph-contrib-panel">
        <p className="ph-contrib-dim">{i18n.t('performance:situation.nextStep')}</p>
        <p className="ph-agent-why">
          {shortText(recommendedAction, 140)}
        </p>
      </div>
    );
  }
  if (splitBlockedMessage) {
    return (
      <div className="ph-contrib-panel">
        <p className="ph-contrib-dim" role="status">
          {shortText(splitBlockedMessage, 56)}
        </p>
        <p className="ph-agent-why">{shortText(fallbackWhy, 120)}</p>
      </div>
    );
  }
  if (!state || state.status === 'idle' || state.status === 'loading') {
    return (
      <div className="ph-contrib-panel" aria-live="polite">
        <p className="ph-contrib-dim ph-contrib-loading">
          {i18n.t('performance:situation.verifying')}
        </p>
        <div className="ph-contrib-skel" aria-hidden>
          <span />
          <span />
        </div>
      </div>
    );
  }
  if (state.status === 'error' || state.refused) {
    return (
      <div className="ph-contrib-panel">
        <p className="ph-contrib-dim" role="status">
          {shortText(state.error || state.why || i18n.t('performance:situation.splitUnavailable'), 48)}
        </p>
        <p className="ph-agent-why">{shortText(fallbackWhy, 120)}</p>
      </div>
    );
  }
  const contrib = state.contribution;
  const rows = contrib?.contributors ?? [];
  const dim = humanDimension(contrib?.dimension);
  const rateMode = contrib?.mode === 'rate' || contrib?.unit === 'pp';
  const mixLab = rateMode
    ? i18n.t('performance:situation.whoBooked')
    : i18n.t('performance:situation.share');
  const rateLab = rateMode
    ? i18n.t('performance:situation.howOften')
    : i18n.t('performance:situation.volume');
  const unitLab = effectUnitLabel(contrib?.unit);
  return (
    <div className="ph-contrib-panel">
      <p className="ph-contrib-dim">
        {i18n.t('performance:situation.whyMoved')}
        <span className="dim">{i18n.t('performance:situation.byDim', { dim })}</span>
        {unitLab ? <span className="unit">{unitLab}</span> : null}
      </p>
      <p className="ph-contrib-key">
        <span className="swatch mix" aria-hidden />
        {mixLab}
        <span className="swatch rate" aria-hidden />
        {rateLab}
        <span className="hint">
          {rateMode
            ? i18n.t('performance:situation.hintRate')
            : i18n.t('performance:situation.hintVolume')}
        </span>
      </p>
      {rows.length > 0 ? (
        <ContributionBars
          rows={rows}
          tip={tip}
          showValues={showValues}
          mixLab={mixLab}
          rateLab={rateLab}
          unit={contrib?.unit}
        />
      ) : (
        <p className="ph-contrib-note">{i18n.t('performance:situation.noBucket')}</p>
      )}
    </div>
  );
}

function humanDimension(dim?: string | null): string {
  switch (dim) {
    case 'guestType':
      return i18n.t('performance:situation.dims.guestType');
    case 'daypart':
      return i18n.t('performance:situation.dims.daypart');
    case 'dayOfWeek':
      return i18n.t('performance:situation.dims.dayOfWeek');
    case 'partySize':
      return i18n.t('performance:situation.dims.partySize');
    case 'source':
      return i18n.t('performance:situation.dims.source');
    case 'leadTime':
      return i18n.t('performance:situation.dims.leadTime');
    case 'regime':
      return i18n.t('performance:situation.dims.regime');
    default:
      return dim || i18n.t('performance:situation.dims.segment');
  }
}

function IconAction({
  icon,
  label,
  shortLabel,
  detail,
  onClick,
  tip,
  showLabel = false,
}: {
  icon: string;
  label: string;
  shortLabel?: string;
  detail: string;
  onClick: () => void;
  tip: ReturnType<typeof useOpsHoverTip>;
  showLabel?: boolean;
}) {
  const visible = shortLabel || label;
  return (
    <button
      type="button"
      className={`ph-agent-ico${showLabel ? ' with-label' : ''}`}
      aria-label={label}
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={e =>
        tip.showAnchored(e.currentTarget, { title: label, text: detail }, 'above')
      }
      onMouseLeave={tip.hide}
      onFocus={e =>
        tip.showAnchored(e.currentTarget, { title: label, text: detail }, 'above')
      }
      onBlur={tip.hide}
    >
      <Icon name={icon} size={13} />
      {showLabel && <span className="ph-agent-ico-lab">{visible}</span>}
    </button>
  );
}

/**
 * Agent situations as compact hero pattern tiles (same visual language as
 * MetricInsightTile / soft signals) — no ph-signal.is-clickable hover layer.
 */
function contribCacheKey(
  cardId: string,
  metric: string,
  periodStart: string,
  periodEnd: string,
  priorStart: string | null | undefined,
  priorEnd: string | null | undefined,
  source: ReservationSourceId[] | null | undefined,
  status: ReservationStatusGroup[] | null | undefined,
): string {
  const src = source?.length ? source.slice().sort().join(',') : '';
  const st = status?.length ? status.slice().sort().join(',') : '';
  const prior = `${priorStart || ''}|${priorEnd || ''}`;
  return `${cardId}|${metric}|${periodStart}|${periodEnd}|${prior}|${src}|${st}`;
}

export function SituationBrief({
  pack,
  loading,
  profile,
  onAct,
  onAsk,
  embedded = false,
  heroChrome = false,
  restaurantId,
  periodStart,
  periodEnd,
  compareStart = null,
  compareEnd = null,
  source = null,
  status = null,
}: {
  pack: SituationPackPayload | undefined;
  loading?: boolean;
  profile?: UserSituationProfile | null;
  onAct: (evidence: SituationEvidence, card: SituationCard) => void;
  /** Opens a new maître d'AI chat; `focus` makes the card its subject. */
  onAsk?: (prompt: string, focus?: AgentFocus) => void;
  embedded?: boolean;
  /** Inside the Performance hero: the hero header carries the name and the
   *  comparison window, so the brief renders only its bands. */
  heroChrome?: boolean;
  restaurantId?: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  /** Scorecard compare window — aligns contribution prior with residual Δ. */
  compareStart?: string | null;
  compareEnd?: string | null;
  /** Match scorecard filters so driverAggCells Redis keys are reused. */
  source?: ReservationSourceId[] | null;
  status?: ReservationStatusGroup[] | null;
}) {
  const { t } = useTranslation('performance');
  const [prefs, setPrefs] = useState<AgentPrefs>(() => loadAgentPrefs());
  const [flippedId, setFlippedId] = useState<string | null>(null);
  const [contribById, setContribById] = useState<Record<string, ContribState>>(
    {},
  );
  const contribFetched = useRef<Set<string>>(new Set());
  const tip = useOpsHoverTip();

  useEffect(() => {
    setFlippedId(null);
  }, [restaurantId, periodStart, periodEnd]);

  const situations = useMemo(() => {
    const raw = pack?.situations ?? [];
    return applyPrefsToSituations(raw, prefs).slice(0, CARD_CAP);
  }, [pack?.situations, prefs]);

  const chips = useMemo(
    () => digInChips(situations, profile),
    [situations, profile],
  );

  const qualityBlocked = Boolean(
    pack?.quality &&
      pack.quality.ok === false &&
      HARD_QUALITY_CODES.has(String(pack.quality.code || '')),
  );
  const softCompare = Boolean(
    pack?.quality &&
      pack.quality.ok === false &&
      pack.quality.code === 'insufficient_compare',
  );
  const quietReasons = pack?.quietReasons ?? [];
  // Every finding dismissed is not the same as nothing found: say which.
  const allDismissed = (pack?.situations?.length ?? 0) > 0 && situations.length === 0;

  // Lazy contribution on flip for covers / noshow / cancel.
  useEffect(() => {
    if (!flippedId) return;
    const card = situations.find(s => s.id === flippedId);
    if (!card || !canDecompose(card)) return;
    const metric = contribMetricKey(card.metricKey);
    if (!metric) return;
    if (card.contribution) {
      setContribById(prev => ({
        ...prev,
        [flippedId]: {
          status: 'ready',
          why: card.why,
          contribution: card.contribution,
        },
      }));
      return;
    }
    if (!restaurantId || !periodStart || !periodEnd) {
      setContribById(prev => ({
        ...prev,
        [flippedId]: {
          status: 'error',
          error: t('situation.missingDates'),
        },
      }));
      return;
    }
    const cacheKey = contribCacheKey(
      flippedId,
      metric,
      periodStart,
      periodEnd,
      compareStart,
      compareEnd,
      source,
      status,
    );
    if (contribFetched.current.has(cacheKey)) return;
    contribFetched.current.add(cacheKey);

    let cancelled = false;
    setContribById(prev => ({
      ...prev,
      [flippedId]: { status: 'loading' },
    }));
    void callMethod<MetricContributionPayload>('opsCommand/getMetricContribution', [
      restaurantId,
      metric,
      periodStart,
      periodEnd,
      null, // regionFilter — parent scope already applied to restaurantId
      source?.length ? source : null,
      status?.length ? status : null,
      null, // dimension — pick best
      compareStart || null,
      compareEnd || null,
    ])
      .then(res => {
        if (cancelled) return;
        setContribById(prev => ({
          ...prev,
          [flippedId]: {
            status: res.refused ? 'error' : 'ready',
            why: res.why,
            contribution: res.contribution,
            refused: res.refused,
            error: res.refused
              ? res.quality?.message || res.why || t('situation.contribRefused')
              : undefined,
          },
        }));
      })
      .catch(err => {
        if (cancelled) return;
        contribFetched.current.delete(cacheKey);
        setContribById(prev => ({
          ...prev,
          [flippedId]: {
            status: 'error',
            error: err instanceof Error ? err.message : t('situation.contribFailed'),
          },
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [
    flippedId,
    restaurantId,
    periodStart,
    periodEnd,
    compareStart,
    compareEnd,
    source,
    status,
    situations,
  ]);

  const flipCard = (cardId: string, currentlyFlipped: boolean) => {
    setFlippedId(currentlyFlipped ? null : cardId);
  };

  const bandCards = {
    signal: situations.filter(s => bandOf(s) === 'signal'),
    trend: situations.filter(s => bandOf(s) === 'trend'),
  };

  const renderItem = (card: SituationCard, i: number) => {
    const tone = severityTone(card.severity);
    const expanded = flippedId === card.id;
    const contrib = contribById[card.id];
    const expandedWhy = whyForExpandedCard(card, contrib);
    const { trend, vs } = card.typeId
      ? { trend: card.insight, vs: null }
      : splitInsightVs(card.insight);
    const sub = card.typeId
      ? card.recommendedAction
      : vs
        ? t('situation.comparedTo', { vs })
        : null;
    return (
      <li
        key={card.id}
        className={`ph-brief-item ${tone}${expanded ? ' is-expanded' : ''}`}
        style={{ animationDelay: `${i * 55}ms` }}
      >
        <div
          className="ph-brief-row"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={t('situation.tileAria', {
            insight: card.insight,
            action: expanded ? t('situation.collapse') : t('situation.expand'),
          })}
          onClick={() => flipCard(card.id, expanded)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              flipCard(card.id, expanded);
            }
          }}
        >
          <span className="ph-brief-ic" aria-hidden>
            <Icon name={severityIcon(card.severity)} size={14} />
          </span>
          <span className="ph-brief-copy">
            <span className="ph-brief-insight">{trend}</span>
            {sub ? <span className="ph-brief-sub">{sub}</span> : null}
          </span>
          <span className="ph-brief-chev" aria-hidden>
            <Icon name="chevronRight" size={14} />
          </span>
        </div>
        <button
          type="button"
          className="ph-brief-dismiss"
          aria-label={t('situation.dismiss')}
          title={t('situation.hideCard')}
          onClick={() => {
            setPrefs(dismissSituation(card.id));
            if (flippedId === card.id) setFlippedId(null);
          }}
        >
          <Icon name="x" size={11} />
        </button>
        {expanded ? (
          <div className="ph-brief-detail">
            <p className="ph-brief-why">{shortText(expandedWhy.why, 260)}</p>
            <ContributionPanel
              state={contrib}
              fallbackWhy={card.why}
              decomposable={canDecompose(card)}
              tip={tip}
              showValues
              recommendedAction={card.typeId ? undefined : card.recommendedAction}
              splitBlockedMessage={
                contrib?.status === 'ready' && !contrib.refused && !expandedWhy.splitOk
                  ? expandedWhy.statusMessage
                  : undefined
              }
            />
            <div className="ph-agent-actions">
              <IconAction
                icon="search"
                label={t('situation.openEvidence')}
                shortLabel={t('situation.evidence')}
                detail={card.recommendedAction}
                tip={tip}
                showLabel
                onClick={() => onAct(card.evidence, card)}
              />
              {onAsk && (
                <IconAction
                  icon="bolt"
                  label={t('situation.askAgent', { name: INSIGHTS_AGENT_NAME })}
                  shortLabel={t('situation.ask')}
                  detail={t('situation.askDetail')}
                  tip={tip}
                  showLabel
                  onClick={() => {
                    const ready =
                      expandedWhy.splitOk &&
                      contrib?.status === 'ready' &&
                      contrib.contribution;
                    const decompHint = ready
                      ? t('situation.decompHintReady', {
                          dimension: contrib.contribution?.dimension,
                          why: contrib.why || '',
                        })
                      : canDecompose(card)
                        ? t('situation.decompHintCall')
                        : '';
                    onAsk(i18n.t('assistant:maitre.exploreInsight'), {
                      kind: 'insight',
                      label: card.insight,
                      detail: `${card.why} Recommended action: ${card.recommendedAction}.${decompHint}`,
                      ...(card.metricKey ? { metricKey: card.metricKey } : {}),
                      ...(card.outletId ? { outletId: card.outletId } : {}),
                    });
                  }}
                />
              )}
              <IconAction
                icon="ban"
                label={t('situation.dismiss')}
                shortLabel={t('situation.dismiss')}
                detail={t('situation.dismissDetail')}
                tip={tip}
                showLabel
                onClick={() => {
                  setPrefs(dismissSituation(card.id));
                  setFlippedId(null);
                }}
              />
            </div>
          </div>
        ) : null}
      </li>
    );
  };

  const band = (kind: 'signal' | 'trend') => {
    const cards = bandCards[kind];
    return (
      <section className={`ph-brief-band is-${kind}`} aria-label={t(kind === 'signal' ? 'hero.needsAttention' : 'hero.trends')}>
        <h3 className="ph-brief-band-lab">
          <Icon name={kind === 'signal' ? 'flag' : 'trendUp'} size={13} />
          {t(kind === 'signal' ? 'hero.needsAttention' : 'hero.trends')}
          {cards.length ? <span className="ph-brief-count">{cards.length}</span> : null}
        </h3>
        {cards.length ? (
          <ul className="ph-brief-list">{cards.map(renderItem)}</ul>
        ) : (
          <p className="ph-brief-band-empty">
            <span className="ph-brief-ok" aria-hidden>
              <Icon name="check" size={12} stroke={2.4} />
            </span>
            {t(kind === 'signal' ? 'hero.noAttention' : 'hero.noTrends')}
          </p>
        )}
      </section>
    );
  };

  const body = (
    <div className="ph-brief">
      {!heroChrome && pack?.compareLabel && !qualityBlocked && !softCompare && (
        <p className="ph-agent-compare" role="note">
          {t('situation.comparedToLabel', {
            // The scorecard's window in the person's language, when its dates are known.
            label: compareStart && compareEnd ? formatRange(compareStart, compareEnd) : pack.compareLabel,
          })}
        </p>
      )}

      {pack?.diagnosing && !qualityBlocked && (
        <div
          className="ph-agent-loader"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <span className="ph-agent-loader-spin" aria-hidden />
          <span className="ph-agent-loader-copy">
            <strong>{t('situation.digging')}</strong>
            <span>{t('situation.diggingBody')}</span>
          </span>
          <span className="ph-agent-loader-track" aria-hidden>
            <span className="ph-agent-loader-bar" />
          </span>
        </div>
      )}

      {pack?.diagnosisFailed && !qualityBlocked && !pack?.diagnosing && (
        <div className="ph-agent-banner ph-agent-banner-soft" role="status">
          <strong>{t('situation.digIncomplete')}</strong>
          <span>{t('situation.digIncompleteBody')}</span>
        </div>
      )}

      {(loading && !pack) || (pack?.diagnosing && situations.length === 0 && !qualityBlocked) ? (
        <div
          className="ph-brief-bands"
          aria-busy
          aria-label={t('situation.loadingAgent', { name: INSIGHTS_AGENT_NAME })}
        >
          {[0, 1].map(col => (
            <div key={col} className="ph-brief-band is-loading" aria-hidden>
              <span className="ph-brief-skel-lab" />
              {[0, 1].map(row => (
                <span
                  key={row}
                  className="ph-brief-skel-row"
                  style={{ animationDelay: `${(col * 2 + row) * 90}ms` }}
                />
              ))}
            </div>
          ))}
        </div>
      ) : qualityBlocked ? (
        <div className="ph-brief-state is-hold" role="status">
          <span className="ph-brief-state-ic" aria-hidden>
            <Icon name="info" size={16} />
          </span>
          <span className="ph-brief-state-copy">
            <strong>{t('situation.qualityHold')}</strong>
            <span>{pack?.quality?.message || pack?.headline}</span>
          </span>
        </div>
      ) : situations.length === 0 ? (
        <div className="ph-brief-state is-quiet" role="status">
          <span className="ph-brief-state-ic" aria-hidden>
            <Icon name="check" size={16} stroke={2.4} />
          </span>
          <span className="ph-brief-state-copy">
            <strong>{allDismissed ? t('situation.allCleared') : t('situation.allQuiet')}</strong>
            <span>
              {allDismissed
                ? t('situation.allClearedBody', { name: INSIGHTS_AGENT_NAME })
                : quietReasons[0] ||
                  t('situation.nothingMaterial', { name: INSIGHTS_AGENT_NAME })}
            </span>
            {!allDismissed && quietReasons.length > 1 && (
              <ul className="ph-agent-quiet-list">
                {quietReasons.slice(1).map(r => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            )}
          </span>
        </div>
      ) : (
        <div
          className="ph-brief-bands"
          aria-label={t('situation.recsAria', { name: INSIGHTS_AGENT_NAME })}
        >
          {band('signal')}
          {band('trend')}
        </div>
      )}

      {chips.length > 0 && onAsk && !loading && pack && (
        <div
          className="ph-agent-starters"
          aria-label={t('situation.starters', { name: INSIGHTS_AGENT_NAME })}
        >
          <div className="ph-agent-starters-lab">
            <Icon name="bolt" size={12} />
            {t('situation.starters', { name: INSIGHTS_AGENT_NAME })}
          </div>
          <div className="ph-agent-chips">
            {chips.map(chip => (
              <button
                key={chip}
                type="button"
                className="ph-agent-chip"
                title={chip}
                onClick={() => onAsk(chip)}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (heroChrome) {
    return (
      <>
        {body}
        <OpsHoverTipPortal tip={tip.tip} />
      </>
    );
  }

  if (embedded) {
    return (
      <div className="ph-group ph-agent-group">
        <div className="ph-group-lab">
          <Icon name="bolt" size={13} />
          {INSIGHTS_AGENT_NAME}
        </div>
        {body}
        <OpsHoverTipPortal tip={tip.tip} />
      </div>
    );
  }

  return (
    <section
      className="ph-agent-standalone"
      aria-label={t('situation.briefAria', { name: INSIGHTS_AGENT_NAME })}
    >
      {body}
      <OpsHoverTipPortal tip={tip.tip} />
    </section>
  );
}

/** The comparison window the brief speaks about, for the hero header. */
export function situationCompareLabel(pack: SituationPackPayload | undefined): string | null {
  if (!pack?.compareLabel) return null;
  if (pack.quality && pack.quality.ok === false) {
    const code = String(pack.quality.code || '');
    if (code === 'insufficient_compare' || HARD_QUALITY_CODES.has(code)) return null;
  }
  return pack.compareLabel;
}
